import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react"

import { PricingProductHeader } from "./PricingProductHeader"
import { PricingTiersTable } from "./PricingTiersTable"
import type { PricingTiersTableHandle } from "./PricingTiersTable"
import { PricingWarehouseHeader } from "./PricingWarehouseHeader"
import type { PricingGridRow } from "./types"

import type {
  IncotermsDefinitionMap,
  PriceChangeItems,
  Product,
  SubmitPriceChangeItemInput,
  UnitOfMeasurement,
} from "@/graphql/codegen/graphql"
import { useMergedIncotermsLocations } from "@/hooks/useMergedIncotermsLocations"
import type { PricingDialogs } from "@/screens/Pricing/constants"
import type { WarehouseHeaderFormValues } from "@/screens/Pricing/schema/WarehouseHeaderSchema"
import { toPriceChangeSubmitItems } from "@/screens/Pricing/utils/priceChangeSubmit"
import { useGetIncotermsQuery } from "@/screens/Procurement/PurchaseOrder/DeliveryTermsAndShippingLegs/hooks/deliveryTerms/useGetIncotermsQuery"
import { AddressFormat } from "@/types/common"
import { isActiveIncotermsLocation } from "@/utils/locations"
import { formatLocationAddress } from "@/utils/utils"

export interface ProductPricingTableHandle {
  getSubmitItems: () => SubmitPriceChangeItemInput[]
}

interface ProductPricingTableProps {
  priceChangeItems: PriceChangeItems
  uoms: UnitOfMeasurement[]
  setDialogOpen: (open: PricingDialogs) => void
  setSelectedProduct: (product: Product) => void
}

export const ProductPricingTable = forwardRef<ProductPricingTableHandle, ProductPricingTableProps>(
  ({ priceChangeItems, uoms, setDialogOpen, setSelectedProduct }, ref) => {
    const tiersTableRef = useRef<PricingTiersTableHandle>(null)

    const [selectedUom, setSelectedUom] = useState<UnitOfMeasurement | undefined>(
      uoms.find((uom) => uom.id === priceChangeItems.product.packageUsWeightUom) ?? undefined
    )
    const [warehouseHeaderValues, setWarehouseHeaderValues] = useState<WarehouseHeaderFormValues>()

    useImperativeHandle(ref, () => ({
      getSubmitItems: () => {
        const rows = tiersTableRef.current?.getAllRowData() ?? []

        return toPriceChangeSubmitItems(rows, priceChangeItems, selectedUom?.id ?? "", warehouseHeaderValues)
      },
    }))

    const { data: incotermsData } = useGetIncotermsQuery()

    const { locations } = useMergedIncotermsLocations(priceChangeItems.product.locationsAssociations ?? [])

    const incotermsOptions = useMemo(
      () =>
        incotermsData
          .filter((incoterm): incoterm is IncotermsDefinitionMap => Boolean(incoterm.key))
          .map((incoterm) => ({ label: incoterm.key as string, value: incoterm.key as string })),
      [incotermsData]
    )

    const incotermsLocationOptions = useMemo(
      () =>
        locations.filter(isActiveIncotermsLocation).map((location) => ({
          label: formatLocationAddress(location, AddressFormat.LocationAndAddress),
          value: location.locationId,
        })),
      [locations]
    )

    const initialRows = useMemo<PricingGridRow[]>(
      () =>
        priceChangeItems.items.flatMap((item) =>
          item.tiers.map<PricingGridRow>((tier) => ({
            ...tier,
            item,
            acceptedCost: priceChangeItems.acceptedCost,
            price: tier.price,
            profit: tier.profit,
            margin: tier.margin,
            newPrice: null,
            newProfit: null,
            newMargin: null,
          }))
        ),
      [priceChangeItems.items, priceChangeItems.acceptedCost]
    )

    const handleUomChange = (id: string) => {
      const nextSelectedUom = uoms.find((uom) => uom.id === id)
      setSelectedUom(nextSelectedUom)
    }

    return (
      <div className='w-full'>
        <PricingProductHeader
          product={priceChangeItems.product}
          uoms={uoms}
          selectedUomId={selectedUom?.id}
          onUomChange={handleUomChange}
          setDialogOpen={setDialogOpen}
          setSelectedProduct={setSelectedProduct}
        />
        <PricingWarehouseHeader
          priceChangeItems={priceChangeItems}
          incotermsOptions={incotermsOptions}
          incotermsLocationOptions={incotermsLocationOptions}
          onApplySelections={setWarehouseHeaderValues}
        />
        <PricingTiersTable ref={tiersTableRef} uomName={selectedUom?.name ?? ""} initialRows={initialRows} />
      </div>
    )
  }
)

ProductPricingTable.displayName = "ProductPricingTable"
